import Pusher from 'pusher-js'
import { getAuthorizationHeaders } from 'lib/helpers/getAuthorizationHeaders/getAuthorizationHeaders'
import { addAction, logError } from 'lib/tracking/datadog'
import { usePusherConnectionStore } from 'lib/hooks/usePusherConnectionStore/usePusherConnectionStore'
import { attachIdleDisconnect } from './helpers/attachIdleDisconnect/attachIdleDisconnect'
import { createPusherLink } from './helpers/createPusherLink/createPusherLink'

interface PusherLinkParams {
  token?: Maybe<string>
}

interface PusherStateChange {
  previous: string
  current: string
}

// Only one Pusher connection is active at a time, but the client (and its Pusher) is recreated on a
// business switch. Dispose the previous instance before wiring the next so a discarded socket neither
// stays open nor reports reconnects.
let disposePrevious: (() => void) | undefined

export const pusherLink = ({ token }: PusherLinkParams = {}) => {
  try {
    const pusher = new Pusher(process.env.PUSHER_APP_KEY, {
      cluster: 'us3',
      authEndpoint: `${process.env.BACKEND_APP_URL}/pusher/auth`,
      auth: { headers: getAuthorizationHeaders({ externalToken: token }) },
    })

    pusher.connection.bind('error', (err: unknown) => {
      logError(err, { name: 'Pusher connection error' })
    })

    let hasConnectedBefore = false

    const handleStateChange = (states: PusherStateChange) => {
      addAction('Pusher connection state change', { from: states.previous, to: states.current })

      if (states.current !== 'connected') {
        return
      }

      if (hasConnectedBefore) {
        usePusherConnectionStore.getState().markReconnected()
      }
      hasConnectedBefore = true
    }

    pusher.connection.bind('state_change', handleStateChange)

    pusher.bind_global((eventName: string, data: unknown) => {
      if (eventName === 'pusher:subscription_error') {
        logError(data, { name: 'Pusher subscription error' })
      }
    })

    disposePrevious?.()
    const detachIdleDisconnect = attachIdleDisconnect({ pusher })

    disposePrevious = () => {
      detachIdleDisconnect()
      pusher.connection.unbind('state_change', handleStateChange)
      pusher.disconnect()
    }

    return createPusherLink({ pusher })
  } catch (err) {
    logError(err, { name: 'Failed to initialize Pusher' })
    throw err
  }
}
